/**
 * Wheel - 转盘核心组件
 *
 * 基于 Figma 设计稿实现的大转盘抽奖组件核心部分。
 * 包含 8 个奖品区块、GO 按钮和装饰元素,支持响应式布局。
 *
 * Figma 设计稿: https://www.figma.com/design/DhWsPamqTM76eMHS0aIQqw
 * 设计节点: 498:10533
 *
 * @module Wheel
 * @date 2025-12-17
 */

'use client'

import React from 'react'
import { cn } from '../../helpers/index.js'
import type { Prize, WheelProps } from './types.js'
import {
  DEFAULT_POINTER_IMAGE,
  DEFAULT_POINTER_DISABLE_IMAGE,
  DEFAULT_WHEEL_BG_6_PRIZES,
  DEFAULT_WHEEL_BG_8_PRIZES,
} from './types.js'
import Picture from '../../components/picture.js'

// ============================================================================
// Wheel 组件
// ============================================================================

/**
 * Wheel 转盘组件
 *
 * 根据 Figma 设计稿精确还原的转盘 UI,包含:
 * - 圆形转盘容器,带边框和背景
 * - 8 个放射状排列的奖品区块
 * - 中心 GO 按钮,带渐变和阴影效果
 * - 顶部指针装饰元素
 * - 响应式尺寸适配 5 个断点
 * - tablet/mobile 使用 CSS clip-path 仅显示上半部分
 */
export const Wheel: React.FC<WheelProps> = ({
  prizes,
  rotation,
  isSpinning,
  isFinished = false,
  isButtonDisabled,
  onGoClick,
  wheelBackgroundImage,
  pointerImage,
  userData,
  className,
}) => {
  // 防御性编程:处理 undefined 或空数组
  if (!prizes || prizes.length === 0) {
    return null
  }

  // 根据奖品数量自动选择默认底图
  const defaultWheelBg = prizes.length === 6 ? DEFAULT_WHEEL_BG_6_PRIZES : DEFAULT_WHEEL_BG_8_PRIZES
  const finalWheelBg = wheelBackgroundImage || defaultWheelBg
  const finalPointerImage =
    pointerImage || (userData?.isLoggedIn ? DEFAULT_POINTER_IMAGE : DEFAULT_POINTER_DISABLE_IMAGE)

  return (
    <div
      className={cn(
        'wheel-wrapper',
        'mx-auto flex items-center justify-center',
        'aspect-w-[1] aspect-h-[1]',
        'l-tablet:size-[340px]',
        className
      )}
    >
      {/* ===================================================================
           转盘容器 - 响应式尺寸 + clip-path 裁剪
          =================================================================== */}
      <div
        className={cn(
          'wheel-container',
          // 响应式尺寸(基于 Figma 设计稿,
          'mx-auto',
          'size-[340px]', // mobile/tablet (default, <768px)
          'lg-desktop:size-[85%] desktop:size-[85%] laptop:size-[90%]' // lg-desktop (≥1921px) - Figma: 800×800
        )}
      >
        {/* =================================================================
             转盘主体 - 应用旋转动画 + 背景图片
            ================================================================= */}
        <div
          className={cn(
            'wheel-disc',
            'absolute inset-0',
            'rounded-full',
            'bg-cover bg-center bg-no-repeat',
            'shadow-[0px_2px_8px_rgba(0,0,0,0.12)]',
            {
              // 抽奖中: 使用快速旋转动画（0.5秒1圈）
              'animate-[spin_0.5s_linear_infinite]': isSpinning && !isFinished,
              // 未抽奖状态: 应用慢速循环旋转（120秒1圈）
              'animate-[spin_120s_linear_infinite]': !isSpinning && !isFinished,
              // 减速动画期间: 移除 transition，使用 requestAnimationFrame 控制
              'transition-none': isFinished,
            }
          )}
          style={{
            transform: isFinished ? `rotate(${rotation}deg)` : undefined,
            backgroundImage: `url('${finalWheelBg}')`,
          }}
          role="img"
          aria-label="大转盘"
        >
          {/* 8 个奖品区块 */}
          {prizes.map((prize, index) => (
            <PrizeSlice key={prize.prizeKey} prize={prize} index={index} totalPrizes={prizes.length} />
          ))}
        </div>

        {/* =================================================================
             指针图片 - 包含 GO 按钮的完整指针图
             图片已包含 GO 按钮样式，代码只负责定位和交互
            ================================================================= */}
        <div
          className={cn(
            'wheel-pointer-wrapper',
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            'z-10',
            // 响应式尺寸 - 根据原图 352×392 比例
            'h-auto w-[72px]', // mobile/tablet (原图缩小2倍)
            'laptop:w-[86px]', // laptop
            'desktop:w-[115px]', // desktop
            'lg-desktop:w-[144px]' // lg-desktop (原图尺寸)
          )}
        >
          <Picture source={finalPointerImage} alt="转盘指针" className="size-full object-contain" />

          {/* 透明的可点击按钮区域 - 覆盖在指针图的 GO 按钮位置 */}
          <button
            onClick={onGoClick}
            disabled={isButtonDisabled}
            className={cn(
              'absolute',
              // GO 按钮在指针图中的位置 (大约在底部中心)
              'bottom-[8%] left-1/2 -translate-x-1/2',
              // 响应式尺寸 - 匹配指针图中的 GO 按钮大小
              'size-[72px]', // mobile/tablet
              'laptop:size-[86px]', // laptop
              'desktop:size-[115px]', // desktop
              'lg-desktop:size-[144px]', // lg-desktop
              'rounded-full',
              'cursor-pointer',
              'transition-transform hover:scale-105 active:scale-95',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
            aria-label="开始抽奖"
            aria-live="polite"
          />
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// PrizeSlice 子组件 - 单个奖品区块
// ============================================================================

interface PrizeSliceProps {
  prize: Prize
  index: number
  totalPrizes: number
}

/**
 * PrizeSlice - 单个奖品区块
 *
 * 放射状排列在转盘上,包含奖品图片和名称。
 * 使用 transform rotate 实现放射状布局,内容反向旋转保持可读。
 */
const PrizeSlice: React.FC<PrizeSliceProps> = ({ prize, index, totalPrizes }) => {
  // 计算当前奖品的旋转角度
  const angle = (360 / totalPrizes) * index

  return (
    <div
      className={cn(
        'prize-slice',
        'absolute left-0 top-0 size-full',
        'flex items-start justify-center',
        'origin-center'
      )}
      style={{
        transform: `rotate(${angle}deg)`,
      }}
    >
      {/* 奖品内容容器 - 反向旋转以保持内容直立 */}
      <div
        className={cn(
          'prize-content',
          'flex flex-col items-center gap-[6px]',
          'pt-[44px]', // 距离圆心的距离 mobile/tablet
          'laptop:pt-[62px]', // laptop
          'desktop:pt-[82px]', // desktop
          'lg-desktop:pt-[104px]' // lg-desktop: 更靠近边缘
        )}
      >
        {/* 奖品名称 */}
        <p
          className={cn(
            'prize-name',
            'text-[12px]',
            'laptop:text-[14px]',
            'laptop-md:text-[16px]',
            'desktop:text-[16px]',
            'lg-desktop:text-[18px]',
            'line-clamp-2 text-center font-semibold leading-[1.2] text-[#080a0f]',
            'w-[80px]',
            'laptop:w-[60px]',
            'laptop-md:w-[100px]',
            'desktop:w-[100px]',
            'lg-desktop:w-[140px]'
          )}
        >
          {prize.name}
        </p>
        {/* 奖品图片 */}
        <Picture
          source={prize.image.url}
          alt={prize.image.alt || `奖品:${prize.name}${prize.rank ? `,排名 ${prize.rank}` : ''}`}
          className={cn(
            'prize-image',
            'size-[45px]', // mobile/tablet
            'laptop-md:size-[64px]', // laptop
            'laptop:size-[64px]', // laptop
            'desktop:size-[86px]', // desktop
            'lg-desktop:size-[108px]', // lg-desktop
            'rounded-lg object-cover'
          )}
        />
      </div>
    </div>
  )
}

// ============================================================================
// 默认导出
// ============================================================================

export default Wheel
